import React from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Divider,
  alpha,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Email,
  AutoAwesome,
  TrendingUp,
  Speed,
  CheckCircle,
  Block,
  ThumbUp,
  Settings,
  Rocket,
  Timer,
  Psychology,
  Lock,
  GitHub,
  LinkedIn,
  Shield,
  Visibility,
  Public,
} from '@mui/icons-material';

const CONTACT_EMAIL = 'contacto.neia@gmail.com';

function LandingPage() {
  const painPoints = [
    {
      icon: <Block color="error" fontSize="large" />,
      title: '¿Pierdes Oportunidades de Negocio?',
      description: 'Leads calificados, cotizaciones urgentes y clientes importantes quedan sin respuesta porque se entierran en tu bandeja de entrada. Cada correo ignorado es dinero perdido.',
      impact: 'Pérdida promedio: $5,000-$50,000 USD en oportunidades mensuales',
    },
    {
      icon: <Timer color="error" fontSize="large" />,
      title: '¿Tu Equipo Pierde Horas Valiosas?',
      description: 'Tu personal desperdicia 2-4 horas diarias clasificando, priorizando y respondiendo los mismos correos repetitivos en lugar de enfocarse en tareas de alto valor.',
      impact: 'Costo promedio: 20-30% de productividad perdida',
    },
    {
      icon: <Email color="error" fontSize="large" />,
      title: '¿Tus Clientes Esperan Demasiado?',
      description: 'Las respuestas lentas frustran a tus clientes y dañan tu reputación. En la era digital, esperar más de 1 hora es inaceptable.',
      impact: '72% de clientes esperan respuesta en menos de 1 hora',
    },
    {
      icon: <TrendingUp color="error" fontSize="large" />,
      title: '¿No Puedes Escalar tu Servicio?',
      description: 'Cada nuevo cliente significa más correos. Contratar más personal es costoso y lento. Tu crecimiento está limitado por tu capacidad de respuesta.',
      impact: 'Costo de contratar: $30,000+ USD anuales por persona',
    },
  ];

  const features = [
    {
      icon: <AutoAwesome sx={{ fontSize: 40 }} color="primary" />,
      title: 'Respuestas Automáticas',
      description: 'La IA contesta correos comunes al instante, liberando tiempo valioso de tu equipo.',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40 }} color="primary" />,
      title: 'Escalamiento Inteligente',
      description: 'Los mensajes críticos se envían directamente a la persona correcta sin intervención manual.',
    },
    {
      icon: <Settings sx={{ fontSize: 40 }} color="primary" />,
      title: 'Reglas Personalizadas',
      description: 'Define exactamente qué hacer por remitente o motivo (escalar, responder, archivar, etc.).',
    },
  ];

  const aiPowers = [
    {
      icon: <Psychology color="secondary" />,
      title: 'Analiza la Intención',
      description: 'Entiende el motivo real del mensaje, incluso si es nuevo.',
    },
    {
      icon: <ThumbUp color="secondary" />,
      title: 'Responde por Sentido Común',
      description: 'Si ninguna regla aplica, la IA genera una respuesta coherente para mantener al cliente atendido.',
    },
  ];

  const steps = [
    {
      number: '1',
      icon: <Email color="primary" />,
      title: 'Conecta',
      description: 'Sincroniza DIALOGIX con tu cuenta de Gmail.',
    },
    {
      number: '2',
      icon: <Settings color="primary" />,
      title: 'Define',
      description: 'Establece tus reglas de negocio (p. ej., razón del mensaje: ventas = escalar a: ventas@tudominio.com).',
    },
    {
      number: '3',
      icon: <Rocket color="primary" />,
      title: 'Olvídate',
      description: 'DIALOGIX es tu asistente 24/7 y hace el resto.',
    },
  ];

  const benefits = [
    '0% de correos sin atención',
    'Clientes felices y satisfechos',
    'Equipo enfocado en el crecimiento',
    'Recupera horas productivas',
    'Nunca pierdas un negocio por un correo olvidado',
    'Mejora la expectativa del cliente',
  ];

  const handleContact = () => {
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=Interés en DIALOGIX`;
  };

  return (
    <Box>
      {/* 1. Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                }}
              >
                DIALOGIX: Automatiza tu Gmail con IA.
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 3,
                  fontWeight: 400,
                  fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                }}
              >
                ¡Deja de perder clientes en tu bandeja de entrada!
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                ¡Recupera horas productivas y asegura cero correos sin respuesta con tu asistente 24/7!
              </Typography>
              <Stack spacing={3}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleContact}
                  sx={{
                    bgcolor: '#059669',
                    color: 'white',
                    px: { xs: 4, md: 6 },
                    py: { xs: 2, md: 2.5 },
                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                    fontWeight: 'bold',
                    boxShadow: '0 8px 24px rgba(5, 150, 105, 0.4)',
                    '&:hover': {
                      bgcolor: '#047857',
                      transform: 'scale(1.05)',
                      boxShadow: '0 12px 32px rgba(5, 150, 105, 0.6)',
                    },
                    transition: 'all 0.3s',
                    borderRadius: 2,
                  }}
                  startIcon={<Email sx={{ fontSize: '2rem !important' }} />}
                >
                  Contáctanos para más información
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1 }}>
                  <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.9rem', md: '1rem' }, fontWeight: 500 }}>
                    Sin compromiso • Te respondemos en menos de 24 horas
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={8}
                sx={{
                  p: 3,
                  bgcolor: alpha('#ffffff', 0.98),
                  borderRadius: 3,
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: '#1e3a8a', fontWeight: 'bold' }}
                >
                  Optimiza tu correo y mejora la expectativa del cliente
                </Typography>
                <List dense>
                  {['0% correos sin respuesta', 'Asistente 24/7', 'Setup en 1 minuto'].map((item, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircle sx={{ color: '#2e7d32' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{ sx: { color: '#212121', fontWeight: 500 } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 2. Video Tutorial */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box
          sx={{
            p: 4,
            bgcolor: alpha('#bfdbfe', 0.2),
            borderRadius: 3,
            textAlign: 'center',
            border: 2,
            borderColor: 'info.main',
          }}
        >
          <Typography
            variant="h3"
            gutterBottom
            fontWeight="bold"
            sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, color: '#1e40af' }}
          >
            Ve DIALOGIX en Acción
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, color: '#4b5563' }}>
            Aprende a configurar DIALOGIX en menos de 3 minutos
          </Typography>
          <Box
            sx={{
              width: '100%',
              maxWidth: 900,
              mx: 'auto',
              position: 'relative',
              paddingBottom: '56.25%',
              height: 0,
              overflow: 'hidden',
              borderRadius: 2,
              boxShadow: 4,
            }}
          >
            <Box
              component="iframe"
              src="https://www.youtube.com/embed/W40VsJkI4Dk"
              title="Tutorial de DIALOGIX"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 0,
              }}
            />
          </Box>
        </Box>
      </Container>

      {/* 3. Screenshots en Acción */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            fontWeight="bold"
            sx={{ fontSize: { xs: '2rem', md: '3rem' }, mb: 2 }}
          >
            Mira DIALOGIX en Acción
          </Typography>
          <Typography variant="h6" textAlign="center" sx={{ mb: 6, color: '#4b5563' }}>
            Interfaz intuitiva y poderosa diseñada para tu productividad
          </Typography>

          <Grid container spacing={4}>
            {[
              {
                webp: '/reglas.webp', png: '/reglas.png',
                alt: 'Panel de Control de DIALOGIX - Reglas de Negocio',
                title: 'Panel de Control Intuitivo',
                desc: 'Configura tus reglas de negocio, visualiza estadísticas en tiempo real y gestiona todas tus automatizaciones desde un solo lugar.',
              },
              {
                webp: '/iaEnaccion.webp', png: '/iaEnacccion.png',
                alt: 'IA respondiendo correos automáticamente',
                title: 'IA en Acción',
                desc: 'Observa cómo la IA analiza, clasifica y responde correos automáticamente, manteniendo el tono profesional de tu marca.',
              },
              {
                webp: '/dashboard.webp', png: '/dashboard.png',
                alt: 'Dashboard de análisis y métricas de DIALOGIX',
                title: 'Análisis Detallados',
                desc: 'Métricas completas sobre tiempos de respuesta, volumen de correos procesados y rendimiento de tus automatizaciones.',
              },
              {
                webp: '/reglas2.webp', png: '/reglas2.png',
                alt: 'Reglas de escalamiento de DIALOGIX',
                title: 'Reglas Personalizadas',
                desc: 'Define criterios específicos para escalar mensajes importantes al equipo correcto automáticamente.',
              },
            ].map((img, i) => (
              <Grid item xs={12} md={6} key={i}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s',
                    '&:hover': { transform: 'translateY(-8px)', boxShadow: 12 },
                  }}
                >
                  <Box sx={{ height: { xs: 250, sm: 300 }, overflow: 'hidden', borderRadius: '4px 4px 0 0' }}>
                    <Box component="picture" sx={{ width: '100%', height: '100%', display: 'block' }}>
                      <source srcSet={img.webp} type="image/webp" />
                      <Box
                        component="img"
                        src={img.png}
                        alt={img.alt}
                        sx={{ width: '100%', height: '100%', objectFit: 'contain', bgcolor: 'grey.100' }}
                      />
                    </Box>
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h5" gutterBottom fontWeight="bold">{img.title}</Typography>
                    <Typography variant="body1" color="text.secondary">{img.desc}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 4. Seguridad y Confianza */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box
          sx={{
            textAlign: 'center',
            mb: 4,
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            bgcolor: alpha('#bbf7d0', 0.15),
            border: 2,
            borderColor: 'success.main',
          }}
        >
          <Shield sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            fontWeight="bold"
            sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, color: '#15803d' }}
          >
            Tu Seguridad es Nuestra Prioridad
          </Typography>
          <Typography variant="h6" sx={{ mb: 6, maxWidth: '900px', mx: 'auto', color: '#4b5563' }}>
            Entendemos que confiar tu correo es una decisión importante. Por eso somos 100% transparentes.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={6}
                sx={{
                  p: 4,
                  height: '100%',
                  border: 2,
                  borderColor: 'success.main',
                  bgcolor: alpha('#e8f5e9', 0.8),
                }}
              >
                <Lock sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ color: '#15803d' }}>
                  Seguridad Garantizada
                </Typography>
                <List sx={{ textAlign: 'left', mt: 3 }}>
                  {[
                    { p: '🔒 Encriptación end-to-end', s: 'Tus datos están protegidos con los más altos estándares de seguridad' },
                    { p: '📜 No vendemos tus datos. Nunca.', s: 'Tu privacidad no está en venta. Punto.' },
                    { p: '🔓 Revoca acceso cuando quieras', s: 'Control total. Puedes desconectar DIALOGIX en cualquier momento.' },
                    { p: '🇲🇽 Empresa mexicana registrada', s: 'Cumplimos con regulaciones locales e internacionales' },
                  ].map((item, i) => (
                    <ListItem key={i}>
                      <ListItemIcon sx={{ minWidth: 40 }}><CheckCircle sx={{ color: 'success.main' }} /></ListItemIcon>
                      <ListItemText primary={item.p} secondary={item.s} primaryTypographyProps={{ fontWeight: 600 }} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={6}
                sx={{
                  p: 4,
                  height: '100%',
                  border: 2,
                  borderColor: 'info.main',
                  bgcolor: alpha('#e3f2fd', 0.8),
                }}
              >
                <Visibility sx={{ fontSize: 60, color: 'info.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ color: '#1e40af' }}>
                  ¿Por qué necesitamos acceso a tu Gmail?
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, fontStyle: 'italic', color: '#4b5563' }}>
                  Transparencia total sobre qué hacemos con tu correo
                </Typography>
                <List sx={{ textAlign: 'left' }}>
                  {[
                    { icon: <CheckCircle sx={{ color: 'info.main' }} />, p: '✅ Para leer correos sin responder', s: 'Solo procesamos mensajes nuevos para analizarlos y responder' },
                    { icon: <CheckCircle sx={{ color: 'info.main' }} />, p: '✅ Para enviar respuestas en tu nombre', s: 'Respondemos automáticamente según tus reglas configuradas' },
                    { icon: <Block sx={{ color: 'error.main' }} />, p: '❌ NO para vender datos', s: 'Jamás compartimos, vendemos o monetizamos tu información', err: true },
                    { icon: <Block sx={{ color: 'error.main' }} />, p: '❌ NO para spam', s: 'Solo enviamos respuestas legítimas a correos recibidos', err: true },
                  ].map((item, i) => (
                    <ListItem key={i}>
                      <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={item.p}
                        secondary={item.s}
                        primaryTypographyProps={{ fontWeight: 600, ...(item.err ? { color: 'error.main' } : {}) }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>

          {/* Sobre el Creador */}
          <Box
            sx={{
              mt: 6,
              p: 4,
              bgcolor: alpha('#f5f5f5', 0.9),
              borderRadius: 3,
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Creado por Anuar Barrera
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center" justifyContent="center" sx={{ mt: 3 }}>
              <Avatar
                src="/profile.webp"
                alt="Anuar Barrera"
                sx={{ width: 100, height: 100, border: 3, borderColor: 'primary.main', boxShadow: 3 }}
              />
              <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                <Typography variant="body1" sx={{ mb: 2, color: '#4b5563' }}>
                  Ingeniero de Software con pasión por crear herramientas que resuelven problemas reales
                </Typography>
                <Stack direction="row" spacing={2} justifyContent={{ xs: 'center', sm: 'flex-start' }}>
                  <IconButton
                    component="a"
                    href="https://www.linkedin.com/in/anuarbarrera"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ bgcolor: '#0077b5', color: 'white', '&:hover': { bgcolor: '#005582' } }}
                  >
                    <LinkedIn />
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://github.com/AnuarBarrera"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ bgcolor: '#24292e', color: 'white', '&:hover': { bgcolor: '#1a1e21' } }}
                  >
                    <GitHub />
                  </IconButton>
                  <Button
                    component="a"
                    href="https://anuarbarrera.dev"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    startIcon={<Public />}
                    sx={{ textTransform: 'none' }}
                  >
                    anuarbarrera.dev
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Container>

      {/* 5. ¿Por qué DIALOGIX vs Google AI? */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box
          sx={{
            textAlign: 'center',
            mb: 4,
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            bgcolor: alpha('#dbeafe', 0.15),
            border: 2,
            borderColor: 'primary.main',
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            fontWeight="bold"
            color="primary"
            sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' } }}
          >
            ¿Por qué DIALOGIX y no solo Google Workspace AI?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, maxWidth: '900px', mx: 'auto', color: '#4b5563' }}>
            Si bien Google Workspace incluye IA, DIALOGIX va más allá con automatización especializada para tu negocio
          </Typography>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 3, height: '100%', bgcolor: alpha('#ffebee', 0.8), border: 1, borderColor: 'grey.300' }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">Google Workspace AI</Typography>
                <List>
                  {[
                    '• Ayuda a redactar correos individuales',
                    '• Requiere intervención manual constante',
                    '• No aprende tus reglas de negocio',
                    '• No escala ni distribuye automáticamente',
                  ].map((t, i) => (
                    <ListItem key={i} sx={{ py: 0.5 }}>
                      <ListItemText primary={t} primaryTypographyProps={{ color: 'text.secondary' }} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={6} sx={{ p: 3, height: '100%', bgcolor: alpha('#e8f5e9', 0.9), border: 2, borderColor: 'success.main' }}>
                <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ color: '#15803d' }}>DIALOGIX</Typography>
                <List>
                  {[
                    'Responde automáticamente 24/7 sin intervención',
                    'Escala mensajes críticos al equipo correcto',
                    'Aprende y aplica tus reglas de negocio personalizadas',
                    'Garantiza 0% de correos sin atención',
                  ].map((t, i) => (
                    <ListItem key={i} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle sx={{ color: '#2e7d32', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary={t} primaryTypographyProps={{ fontWeight: 500 }} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, p: 2, bgcolor: alpha('#e3f2fd', 0.8), borderRadius: 2, border: 1, borderColor: 'info.main' }}>
            <Typography variant="body1" fontWeight="500">
              <strong>Compatibilidad:</strong> Actualmente disponible para Gmail.
              <Box component="span" sx={{ color: '#1e40af', fontWeight: 'bold', ml: 1 }}>
                Próximamente: Outlook, Yahoo Mail y otros clientes de correo
              </Box>
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* 6. Pain Points */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          fontWeight="bold"
          color="error.main"
          sx={{ fontSize: { xs: '2rem', md: '3rem' }, mb: 2 }}
        >
          ¿Te Sientes Identificado con Estos Problemas?
        </Typography>
        <Typography variant="h6" textAlign="center" sx={{ mb: 6, maxWidth: '800px', mx: 'auto', color: '#4b5563' }}>
          Estos son los desafíos reales que enfrentan las empresas que dependen del correo electrónico
        </Typography>

        <Grid container spacing={4}>
          {painPoints.map((pain, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  height: '100%',
                  p: 3,
                  border: 2,
                  borderColor: 'error.main',
                  bgcolor: alpha('#ffcdd2', 0.1),
                  transition: 'all 0.3s',
                  '&:hover': { transform: 'translateY(-8px)', boxShadow: 8 },
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {pain.icon}
                    <Typography variant="h5" sx={{ ml: 2, fontWeight: 'bold', color: 'error.main' }}>
                      {pain.title}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    {pain.description}
                  </Typography>
                  <Box sx={{ mt: 2, p: 2, bgcolor: alpha('#ffcdd2', 0.5), borderRadius: 1, borderLeft: 4, borderColor: 'error.dark' }}>
                    <Typography variant="body2" fontWeight="bold" color="error.dark">{pain.impact}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: alpha('#e8f5e9', 0.9),
            borderRadius: 3,
            textAlign: 'center',
            border: 2,
            borderColor: 'success.main',
          }}
        >
          <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ color: '#15803d' }}>
            La Buena Noticia: DIALOGIX Resuelve Todos Estos Problemas
          </Typography>
          <Typography variant="h6" sx={{ color: '#4b5563' }}>
            Automatiza tu correo, recupera tu tiempo y nunca pierdas otra oportunidad
          </Typography>
        </Box>
      </Container>

      {/* 7. Solution */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            fontWeight="bold"
            sx={{ fontSize: { xs: '2rem', md: '3rem' } }}
          >
            DIALOGIX: Nunca Vuelvas a Perder un Negocio por un Correo Olvidado
          </Typography>
          <Typography variant="h6" textAlign="center" sx={{ mb: 6, color: '#4b5563' }}>
            DIALOGIX no es solo un filtro; es un asistente inteligente que toma decisiones por ti, basado en tus reglas de negocio:
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    p: 3,
                    textAlign: 'center',
                    border: 2,
                    borderColor: 'primary.main',
                    bgcolor: alpha('#dbeafe', 0.2),
                    transition: 'all 0.3s',
                    '&:hover': { transform: 'scale(1.05)', boxShadow: 8 },
                  }}
                >
                  <CardContent>
                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                    <Typography variant="h5" gutterBottom fontWeight="bold" color="primary">{feature.title}</Typography>
                    <Typography variant="body1" color="text.secondary">{feature.description}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 8. AI Power */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box
          sx={{
            textAlign: 'center',
            mb: 6,
            p: 4,
            borderRadius: 3,
            bgcolor: alpha('#f3e8ff', 0.2),
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            fontWeight="bold"
            color="secondary"
            sx={{ fontSize: { xs: '2rem', md: '3rem' } }}
          >
            El Factor Cero Abandono
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, color: '#4b5563' }}>
            Gracias a la potencia de Gemini (Google AI), DIALOGIX no se limita a tus reglas:
          </Typography>

          <Grid container spacing={4} sx={{ mt: 2 }} justifyContent="center">
            {aiPowers.map((power, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper elevation={4} sx={{ p: 4, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {power.icon}
                    <Typography variant="h5" sx={{ ml: 2, fontWeight: 'bold' }}>{power.title}</Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary">{power.description}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 4, p: 3, bgcolor: '#2e7d32', borderRadius: 2, color: 'white', boxShadow: 3 }}>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
              Resultado: 0% de correos sin atención. Clientes felices y tu equipo enfocado en el crecimiento.
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* 9. Benefits */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          fontWeight="bold"
          color="primary"
          sx={{ fontSize: { xs: '2rem', md: '3rem' }, mb: 2 }}
        >
          ¿Por qué elegir DIALOGIX?
        </Typography>
        <Typography variant="h6" textAlign="center" sx={{ mb: 6, maxWidth: '900px', mx: 'auto', color: '#4b5563' }}>
          La única solución completa de automatización de correo con IA que aprende tu negocio
        </Typography>

        <Grid container spacing={4} sx={{ mb: 6 }}>
          {[
            { icon: <Speed sx={{ fontSize: 70, color: 'success.main', mb: 2 }} />, stat: '10x', label: 'Más Rápido', desc: 'Respuestas instantáneas 24/7 sin intervención humana. Reduce tiempos de respuesta de horas a segundos.', color: 'success' },
            { icon: <CheckCircle sx={{ fontSize: 70, color: 'primary.main', mb: 2 }} />, stat: '100%', label: 'Cobertura Total', desc: 'Garantiza 0% de correos sin atención. Cada mensaje recibe una respuesta apropiada o se escala correctamente.', color: 'primary' },
            { icon: <Psychology sx={{ fontSize: 70, color: 'warning.main', mb: 2 }} />, stat: 'Smart', label: 'IA Personalizada', desc: 'Aprende tus reglas de negocio y el contexto de tu empresa para respuestas precisas y profesionales.', color: 'warning' },
          ].map((item, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Paper
                elevation={8}
                sx={{
                  p: 4,
                  height: '100%',
                  textAlign: 'center',
                  border: 3,
                  borderColor: `${item.color}.main`,
                  transition: 'all 0.3s',
                  '&:hover': { transform: 'scale(1.05)', boxShadow: 12 },
                }}
              >
                {item.icon}
                <Typography variant="h4" gutterBottom fontWeight="bold" color={`${item.color}.main`}>{item.stat}</Typography>
                <Typography variant="h6" gutterBottom fontWeight="bold">{item.label}</Typography>
                <Typography variant="body1" color="text.secondary">{item.desc}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper elevation={6} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
          <Typography variant="h4" textAlign="center" gutterBottom fontWeight="bold" color="primary" sx={{ fontSize: { xs: '1.8rem', md: '2.125rem' }, mb: 4 }}>
            Ventajas Competitivas
          </Typography>
          <Grid container spacing={3}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircle sx={{ color: '#2e7d32', mr: 2, fontSize: 28, flexShrink: 0 }} />
                  <Typography variant="body1" fontWeight="500">{benefit}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ p: 3, bgcolor: alpha('#fff9c4', 0.8), borderRadius: 2, border: 2, borderColor: 'warning.main' }}>
            <Typography variant="h5" gutterBottom fontWeight="bold" textAlign="center" sx={{ color: '#b45309' }}>
              ROI Comprobado
            </Typography>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {[
                { n: '75%', l: 'Reducción en tiempo de gestión de correos' },
                { n: '95%', l: 'Tasa de satisfacción del cliente' },
                { n: '$20K+', l: 'Ahorro promedio anual en costos operativos' },
              ].map((r, i) => (
                <Grid item xs={12} sm={4} key={i}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: '#15803d' }}>{r.n}</Typography>
                    <Typography variant="body2" color="text.secondary">{r.l}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Paper>
      </Container>

      {/* 10. How it Works */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            fontWeight="bold"
            sx={{ fontSize: { xs: '2rem', md: '3rem' } }}
          >
            Comienza en 1 Minuto
          </Typography>
          <Typography variant="h6" textAlign="center" sx={{ mb: 6, color: '#4b5563' }}>
            Tres simples pasos para transformar tu gestión de correos
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {steps.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    p: { xs: 3, md: 4 },
                    textAlign: 'center',
                    transition: 'all 0.3s',
                    '&:hover': { transform: 'translateY(-8px)', boxShadow: 8 },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        width: { xs: 60, md: 70 },
                        height: { xs: 60, md: 70 },
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto',
                        mb: 3,
                        fontSize: { xs: '2rem', md: '2.5rem' },
                        fontWeight: 'bold',
                      }}
                    >
                      {step.number}
                    </Box>
                    <Box sx={{ mb: 2 }}>{step.icon}</Box>
                    <Typography variant="h5" gutterBottom fontWeight="bold" color="primary" sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                      {step.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 11. Final CTA */}
      <Box
        sx={{
          background: `linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)`,
          color: 'white',
          py: 10,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Email sx={{ fontSize: { xs: 60, md: 80 }, mb: 3 }} />
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            fontWeight="bold"
            sx={{ fontSize: { xs: '2rem', md: '3rem' } }}
          >
            ¿Interesado en DIALOGIX?
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, fontWeight: 300, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
            Contáctanos y te informamos sobre disponibilidad y próximos pasos
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleContact}
            sx={{
              bgcolor: '#059669',
              color: 'white',
              px: { xs: 5, md: 7 },
              py: { xs: 2, md: 2.5 },
              fontSize: { xs: '1.2rem', md: '1.4rem' },
              fontWeight: 'bold',
              '&:hover': { bgcolor: '#047857', transform: 'scale(1.05)' },
              transition: 'all 0.3s',
              boxShadow: '0 8px 32px rgba(5, 150, 105, 0.5)',
              borderRadius: 2,
            }}
            startIcon={<Email sx={{ fontSize: '2rem !important' }} />}
          >
            Contáctanos
          </Button>
          <Typography variant="body2" sx={{ mt: 3, opacity: 0.9 }}>
            {CONTACT_EMAIL} • Respondemos en menos de 24 horas
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

export default LandingPage;
